import React, { useRef } from 'react'
import { Link } from 'react-router-dom'
import { motion, useInView } from 'framer-motion'
import { useNavbarScroll } from '../hooks/useOptimizedScroll'
import { useIsMobile } from '../hooks/useMediaQuery'
import { PageMobileFooter } from './layout/PageMobileFooter'
import { aboutPageFooter } from './layout/footerConfigs'
import { SEOHead } from './SEO/SEOHead'
import { getStructuredData } from '../config/structuredData'

export const AboutPage: React.FC = () => {
  const isMobile = useIsMobile()

  const prefersReducedMotion = useRef(
    typeof window !== 'undefined'
      ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
      : false
  )

  // Scroll to top when component mounts
  React.useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  // Use optimized navbar scroll hook to prevent flickering
  useNavbarScroll()

  return (
    <>
      {/* SEO Head */}
      <SEOHead
        customTitle="About - Mernaki"
        customDescription="Learn about Mernaki's mission to revolutionize recruitment technology, enterprise solutions, and innovation across software, hardware, and compliance."
        customMetadata={{
          structuredData: getStructuredData('about')
        }}
      />

      {/* Navbar is now global - no individual page navbars needed */}

      {/* Main Content Wrapper with Scroll-Over Effect */}
      <div
        className="min-h-screen relative"
        style={{
          backgroundColor: '#F7F5F5',
          borderBottomLeftRadius: '48px',
          borderBottomRightRadius: '48px',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.2)',
          overflow: 'hidden',
          zIndex: 10,
          paddingBottom: '100px'
        }}
      >

        {/* Hero Section */}
        <HeroSection prefersReducedMotion={prefersReducedMotion.current} />

        {/* Vision & Mission */}
        <VisionMissionSection prefersReducedMotion={prefersReducedMotion.current} />

        {/* Our Ecosystem */}
        <EcosystemSection prefersReducedMotion={prefersReducedMotion.current} />

        {/* The Story */}
        <StorySection prefersReducedMotion={prefersReducedMotion.current} />

        {/* The Founder */}
        <FounderSection prefersReducedMotion={prefersReducedMotion.current} />

        {/* Philosophy */}
        <PhilosophySection prefersReducedMotion={prefersReducedMotion.current} />

        {/* Global Reach */}
        <GlobalReachSection prefersReducedMotion={prefersReducedMotion.current} />

        {/* CTA */}
        <CTASection prefersReducedMotion={prefersReducedMotion.current} />
      </div>

      {/* Footer */}
      <FooterSection isMobile={isMobile} />
    </>
  )
}

// Component Sections
const HeroSection: React.FC<{ prefersReducedMotion: boolean }> = ({ prefersReducedMotion }) => {
  const ref = useRef(null)
  const isInView = useInView(ref, { once: false, amount: 0.3 })

  // Character-by-character animation variants
  const headlineText = "We build the future — in code, design, and innovation."
  const headlineVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.03,
        delayChildren: 0.2
      }
    }
  }

  const charVariants = {
    hidden: { opacity: 0, x: -20, y: 10 },
    visible: {
      opacity: 1,
      x: 0,
      y: 0,
      transition: {
        duration: 0.5,
        ease: [0.4, 0.0, 0.2, 1]
      }
    }
  }

  // Subtle floating animation for headline (after reveal)
  const floatingVariants = {
    animate: {
      y: [0, -8, 0],
      transition: {
        duration: 4,
        repeat: Infinity,
        ease: "easeInOut",
        delay: 2
      }
    }
  }

  return (
    <section ref={ref} className="relative pt-48 pb-32 overflow-hidden" style={{ backgroundColor: '#F7F5F5' }}>
      {/* Animated Background Gradient Orbs */}
      <motion.div className="absolute -top-40 -left-40 w-96 h-96 rounded-full opacity-20 blur-3xl" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', willChange: prefersReducedMotion ? 'auto' : 'transform' }} animate={prefersReducedMotion ? {} : { scale: [1, 1.2, 1], x: [0, 50, 0], y: [0, 30, 0] }} transition={{ duration: 20, repeat: Infinity, ease: 'easeInOut' }} />
      <motion.div className="absolute top-1/2 -right-40 w-96 h-96 rounded-full opacity-20 blur-3xl" style={{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', willChange: prefersReducedMotion ? 'auto' : 'transform' }} animate={prefersReducedMotion ? {} : { scale: [1, 1.3, 1], x: [0, -50, 0], y: [0, -30, 0] }} transition={{ duration: 25, repeat: Infinity, ease: 'easeInOut' }} />
      <motion.div className="absolute bottom-20 left-1/3 w-80 h-80 rounded-full opacity-15 blur-3xl" style={{ background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', willChange: prefersReducedMotion ? 'auto' : 'transform' }} animate={prefersReducedMotion ? {} : { scale: [1, 1.15, 1], x: [0, 30, 0], y: [0, -20, 0] }} transition={{ duration: 18, repeat: Infinity, ease: 'easeInOut' }} />

      <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        {/* Character-by-Character Headline Reveal with Floating Animation */}
        <motion.div
          animate={prefersReducedMotion ? {} : (isInView ? "animate" : {})}
          variants={floatingVariants}
        >
          <motion.h1
            className="text-5xl md:text-6xl lg:text-7xl mb-8 leading-tight"
            style={{
              color: '#1A1A1A',
              fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
              fontWeight: 600,
              letterSpacing: '-0.03em'
            }}
            initial={prefersReducedMotion ? "visible" : "hidden"}
            animate={isInView ? "visible" : "hidden"}
            variants={headlineVariants}
          >
            {headlineText.split(' ').map((word, wordIndex) => (
              <span key={`word-${wordIndex}`} style={{ display: 'inline-block', whiteSpace: 'nowrap' }}>
                {word.split('').map((char, charIndex) => (
                  <motion.span
                    key={`${wordIndex}-${charIndex}`}
                    variants={prefersReducedMotion ? {} : charVariants}
                    style={{ display: 'inline-block' }}
                  >
                    {char}
                  </motion.span>
                ))}
                {wordIndex < headlineText.split(' ').length - 1 && (
                  <span style={{ display: 'inline-block', width: '0.3em' }}>&nbsp;</span>
                )}
              </span>
            ))}
          </motion.h1>
        </motion.div>

        <motion.p className="text-xl md:text-2xl max-w-3xl mx-auto leading-relaxed" style={{ color: '#666666', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 500 }} initial={prefersReducedMotion ? { opacity: 1 } : { opacity: 0, y: 20 }} animate={isInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.8, delay: 1.5, ease: [0.4, 0.0, 0.2, 1] }}>
          From AI-driven software to next-generation devices, Mernaki creates technology that bridges creativity, intelligence, and simplicity.
        </motion.p>
      </div>
    </section>
  )
}


const VisionMissionSection: React.FC<{ prefersReducedMotion: boolean }> = ({ prefersReducedMotion }) => {
  const ref = useRef(null)
  const isInView = useInView(ref, { once: false, amount: 0.3 })

  return (
    <section ref={ref} className="relative py-32 overflow-hidden" style={{ backgroundColor: 'transparent' }}>
      {/* Animated Background Gradient Orbs - Continuing from Hero */}
      <motion.div className="absolute -top-20 right-0 w-96 h-96 rounded-full opacity-20 blur-3xl" style={{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', willChange: prefersReducedMotion ? 'auto' : 'transform' }} animate={prefersReducedMotion ? {} : { scale: [1, 1.2, 1], x: [0, -30, 0], y: [0, 20, 0] }} transition={{ duration: 22, repeat: Infinity, ease: 'easeInOut' }} />
      <motion.div className="absolute top-1/3 -left-20 w-80 h-80 rounded-full opacity-15 blur-3xl" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', willChange: prefersReducedMotion ? 'auto' : 'transform' }} animate={prefersReducedMotion ? {} : { scale: [1, 1.15, 1], x: [0, 40, 0], y: [0, -15, 0] }} transition={{ duration: 20, repeat: Infinity, ease: 'easeInOut' }} />

      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-16">
          <motion.div initial={prefersReducedMotion ? { opacity: 1 } : { opacity: 0, x: -30 }} animate={isInView ? { opacity: 1, x: 0 } : {}} transition={{ duration: 0.8, ease: [0.4, 0.0, 0.2, 1] }}>
            <h2 className="text-3xl md:text-4xl mb-6" style={{ color: '#1A1A1A', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 600, letterSpacing: '-0.02em' }}>Vision</h2>
            <p className="text-lg leading-relaxed" style={{ color: '#666666', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 500 }}>To empower creators, developers, and companies through seamless technology that feels human.</p>
          </motion.div>
          <motion.div initial={prefersReducedMotion ? { opacity: 1 } : { opacity: 0, x: 30 }} animate={isInView ? { opacity: 1, x: 0 } : {}} transition={{ duration: 0.8, delay: 0.2, ease: [0.4, 0.0, 0.2, 1] }}>
            <h2 className="text-3xl md:text-4xl mb-6" style={{ color: '#1A1A1A', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 600, letterSpacing: '-0.02em' }}>Mission</h2>
            <p className="text-lg leading-relaxed" style={{ color: '#666666', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 500 }}>We design intelligent tools, devices, and automation systems that help people and businesses move faster, create more, and waste less.</p>
          </motion.div>
        </div>
      </div>
    </section>
  )
}

const EcosystemSection: React.FC<{ prefersReducedMotion: boolean }> = ({ prefersReducedMotion }) => {
  const ref = useRef(null)
  const isInView = useInView(ref, { once: false, amount: 0.2 })

  return (
    <section ref={ref} className="relative py-32 overflow-hidden" style={{ backgroundColor: 'transparent' }}>
      {/* Animated Background Gradient Orbs */}
      <motion.div className="absolute top-1/4 -right-32 w-96 h-96 rounded-full opacity-20 blur-3xl" style={{ background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', willChange: prefersReducedMotion ? 'auto' : 'transform' }} animate={prefersReducedMotion ? {} : { scale: [1, 1.25, 1], x: [0, -40, 0], y: [0, 30, 0] }} transition={{ duration: 24, repeat: Infinity, ease: 'easeInOut' }} />
      <motion.div className="absolute -top-40 -left-20 w-80 h-80 rounded-full opacity-15 blur-3xl" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', willChange: prefersReducedMotion ? 'auto' : 'transform' }} animate={prefersReducedMotion ? {} : { scale: [1, 1.15, 1], x: [0, 40, 0], y: [0, -15, 0] }} transition={{ duration: 20, repeat: Infinity, ease: 'easeInOut' }} />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.h2 className="text-4xl md:text-5xl text-center mb-16" style={{ color: '#1A1A1A', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 600, letterSpacing: '-0.03em' }} initial={prefersReducedMotion ? { opacity: 1 } : { opacity: 0, y: 20 }} animate={isInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.8, ease: [0.4, 0.0, 0.2, 1] }}>Our Ecosystem</motion.h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <motion.div initial={prefersReducedMotion ? { opacity: 1 } : { opacity: 0, y: 30 }} animate={isInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.6, ease: [0.4, 0.0, 0.2, 1] }}>
            <div className="p-8 rounded-2xl" style={{ backgroundColor: 'rgba(255, 255, 255, 0.6)', minHeight: '400px' }}>
              <div className="w-16 h-16 mb-6 rounded-2xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', boxShadow: '0 8px 24px rgba(102, 126, 234, 0.25)' }}>
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path>
                  <polyline points="7.5 4.21 12 6.81 16.5 4.21"></polyline>
                  <polyline points="7.5 19.79 7.5 14.6 3 12"></polyline>
                  <polyline points="21 12 16.5 14.6 16.5 19.79"></polyline>
                  <polyline points="3.27 6.96 12 12.01 20.73 6.96"></polyline>
                  <line x1="12" y1="22.08" x2="12" y2="12"></line>
                </svg>
              </div>
              <h3 className="text-2xl mb-6" style={{ color: '#1A1A1A', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 600 }}>Software</h3>
              <ul className="space-y-4">
                <li><div className="text-sm font-semibold mb-1" style={{ color: 'rgba(26, 26, 26, 0.5)', filter: 'blur(2px)', userSelect: 'none', pointerEvents: 'none' }}>Project Crimson</div><div className="text-xs" style={{ color: '#666666' }}>A next-gen AI IDE for creators</div></li>
                <li><div className="text-sm font-semibold mb-1" style={{ color: 'rgba(26, 26, 26, 0.5)', filter: 'blur(2px)', userSelect: 'none', pointerEvents: 'none' }}>Project Nexus</div><div className="text-xs" style={{ color: '#666666' }}>Immersive creative platform</div></li>
                <li><div className="text-sm font-semibold mb-1" style={{ color: 'rgba(26, 26, 26, 0.5)', filter: 'blur(2px)', userSelect: 'none', pointerEvents: 'none' }}>Project Velocity</div><div className="text-xs" style={{ color: '#666666' }}>Advanced system optimization</div></li>
              </ul>
            </div>
          </motion.div>
          <motion.div initial={prefersReducedMotion ? { opacity: 1 } : { opacity: 0, y: 30 }} animate={isInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.6, delay: 0.15, ease: [0.4, 0.0, 0.2, 1] }}>
            <div className="p-8 rounded-2xl" style={{ backgroundColor: 'rgba(255, 255, 255, 0.6)', minHeight: '400px' }}>
              <div className="w-16 h-16 mb-6 rounded-2xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', boxShadow: '0 8px 24px rgba(240, 147, 251, 0.25)' }}>
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="4" y="4" width="16" height="16" rx="2" ry="2"></rect>
                  <rect x="9" y="9" width="6" height="6"></rect>
                  <line x1="9" y1="1" x2="9" y2="4"></line>
                  <line x1="15" y1="1" x2="15" y2="4"></line>
                  <line x1="9" y1="20" x2="9" y2="23"></line>
                  <line x1="15" y1="20" x2="15" y2="23"></line>
                  <line x1="20" y1="9" x2="23" y2="9"></line>
                  <line x1="20" y1="14" x2="23" y2="14"></line>
                  <line x1="1" y1="9" x2="4" y2="9"></line>
                  <line x1="1" y1="14" x2="4" y2="14"></line>
                </svg>
              </div>
              <h3 className="text-2xl mb-6" style={{ color: '#1A1A1A', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 600 }}>Hardware</h3>
              <ul className="space-y-4">
                <li><div className="text-sm font-semibold mb-1" style={{ color: 'rgba(26, 26, 26, 0.5)', filter: 'blur(2px)', userSelect: 'none', pointerEvents: 'none' }}>Project Quantum</div><div className="text-xs" style={{ color: '#666666' }}>The future of portable computing</div></li>
                <li><div className="text-sm font-semibold mb-1" style={{ color: 'rgba(26, 26, 26, 0.5)', filter: 'blur(2px)', userSelect: 'none', pointerEvents: 'none' }}>Project Resonance</div><div className="text-xs" style={{ color: '#666666' }}>Biometric-aware technology</div></li>
              </ul>
            </div>
          </motion.div>
          <motion.div initial={prefersReducedMotion ? { opacity: 1 } : { opacity: 0, y: 30 }} animate={isInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.6, delay: 0.3, ease: [0.4, 0.0, 0.2, 1] }}>
            <div className="p-8 rounded-2xl" style={{ backgroundColor: 'rgba(255, 255, 255, 0.6)', minHeight: '400px' }}>
              <div className="w-16 h-16 mb-6 rounded-2xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', boxShadow: '0 8px 24px rgba(79, 172, 254, 0.25)' }}>
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="3"></circle>
                  <path d="M12 1v6m0 6v6m8.66-13.66l-4.24 4.24m-4.24 4.24l-4.24 4.24M23 12h-6m-6 0H1m20.66 8.66l-4.24-4.24m-4.24-4.24l-4.24-4.24"></path>
                </svg>
              </div>
              <h3 className="text-2xl mb-6" style={{ color: '#1A1A1A', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 600 }}>Services</h3>
              <ul className="space-y-4">
                <li><div className="text-sm font-semibold mb-1" style={{ color: '#1A1A1A' }}>Job Automation</div><div className="text-xs" style={{ color: '#666666' }}>Streamlined hiring solutions</div></li>
                <li><div className="text-sm font-semibold mb-1" style={{ color: '#1A1A1A' }}>Rare Earth Permits</div><div className="text-xs" style={{ color: '#666666' }}>Compliance automation</div></li>
                <li><div className="text-sm font-semibold mb-1" style={{ color: 'rgba(26, 26, 26, 0.5)', filter: 'blur(2px)', userSelect: 'none', pointerEvents: 'none' }}>VerpackG Deletion</div><div className="text-xs" style={{ color: '#666666' }}>Regulatory compliance</div></li>
                <li><div className="text-sm font-semibold mb-1" style={{ color: 'rgba(26, 26, 26, 0.5)', filter: 'blur(2px)', userSelect: 'none', pointerEvents: 'none' }}>Hiring Pages</div><div className="text-xs" style={{ color: '#666666' }}>Custom recruitment platforms</div></li>
              </ul>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  )
}


const StorySection: React.FC<{ prefersReducedMotion: boolean }> = ({ prefersReducedMotion }) => {
  const ref = useRef(null)
  const isInView = useInView(ref, { once: false, amount: 0.3 })

  return (
    <section ref={ref} className="relative py-32" style={{ backgroundColor: 'transparent' }}>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <motion.h2 className="text-4xl md:text-5xl mb-8" style={{ color: '#1A1A1A', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 600, letterSpacing: '-0.03em' }} initial={prefersReducedMotion ? { opacity: 1 } : { opacity: 0, y: 20 }} animate={isInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.8, ease: [0.4, 0.0, 0.2, 1] }}>The Story</motion.h2>
        <motion.p className="text-lg md:text-xl leading-relaxed mb-6" style={{ color: '#666666', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 500 }} initial={prefersReducedMotion ? { opacity: 1 } : { opacity: 0, y: 20 }} animate={isInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.8, delay: 0.2, ease: [0.4, 0.0, 0.2, 1] }}>Mernaki began as a project to modernize hiring. It quickly became something much larger — a hub of innovation across AI, design, and hardware.</motion.p>
        <motion.p className="text-lg md:text-xl leading-relaxed" style={{ color: '#666666', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 500 }} initial={prefersReducedMotion ? { opacity: 1 } : { opacity: 0, y: 20 }} animate={isInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.8, delay: 0.4, ease: [0.4, 0.0, 0.2, 1] }}>Today, we build everything from developer tools to advanced devices — all under one mission: to create technology that empowers, not overwhelms.</motion.p>
      </div>
    </section>
  )
}

const FounderSection: React.FC<{ prefersReducedMotion: boolean }> = ({ prefersReducedMotion }) => {
  const ref = useRef(null)
  const isInView = useInView(ref, { once: false, amount: 0.3 })

  return (
    <section ref={ref} className="relative py-32" style={{ backgroundColor: 'transparent' }}>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <motion.h2 className="text-4xl md:text-5xl mb-8" style={{ color: '#1A1A1A', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 600, letterSpacing: '-0.03em' }} initial={prefersReducedMotion ? { opacity: 1 } : { opacity: 0, y: 20 }} animate={isInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.8, ease: [0.4, 0.0, 0.2, 1] }}>The Studio</motion.h2>
        <motion.p className="text-lg md:text-xl leading-relaxed mb-4" style={{ color: '#666666', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 500 }} initial={prefersReducedMotion ? { opacity: 1 } : { opacity: 0, y: 20 }} animate={isInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.8, delay: 0.2, ease: [0.4, 0.0, 0.2, 1] }}>Founded by Dallas ND, Mernaki operates as a digital innovation studio combining deep software engineering, AI design, and futuristic product thinking.</motion.p>
        <motion.p className="text-base" style={{ color: '#999999', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 500 }} initial={prefersReducedMotion ? { opacity: 1 } : { opacity: 0, y: 20 }} animate={isInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.8, delay: 0.4, ease: [0.4, 0.0, 0.2, 1] }}>Based in Germany — connected to the world.</motion.p>
      </div>
    </section>
  )
}

const PhilosophySection: React.FC<{ prefersReducedMotion: boolean }> = ({ prefersReducedMotion }) => {
  const ref = useRef(null)
  const isInView = useInView(ref, { once: false, amount: 0.3 })

  return (
    <section ref={ref} className="relative py-32" style={{ backgroundColor: '#1A1A1A' }}>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <motion.h2 className="text-4xl md:text-5xl mb-12" style={{ color: '#FFFFFF', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 600, letterSpacing: '-0.03em' }} initial={prefersReducedMotion ? { opacity: 1 } : { opacity: 0, y: 20 }} animate={isInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.8, ease: [0.4, 0.0, 0.2, 1] }}>We believe technology should feel natural.</motion.h2>
        <motion.div className="space-y-6" initial={prefersReducedMotion ? { opacity: 1 } : { opacity: 0, y: 20 }} animate={isInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.8, delay: 0.2, ease: [0.4, 0.0, 0.2, 1] }}>
          <p className="text-2xl md:text-3xl" style={{ color: 'rgba(255, 255, 255, 0.9)', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 500, letterSpacing: '-0.01em' }}>Simplicity. Speed. Symmetry.</p>
          <p className="text-lg" style={{ color: 'rgba(255, 255, 255, 0.6)', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 400 }}>Every line of code, every surface, every motion — designed to feel right.</p>
        </motion.div>
      </div>
    </section>
  )
}

const GlobalReachSection: React.FC<{ prefersReducedMotion: boolean }> = ({ prefersReducedMotion }) => {
  const ref = useRef(null)
  const isInView = useInView(ref, { once: false, amount: 0.5 })

  return (
    <section ref={ref} className="relative py-24" style={{ backgroundColor: 'transparent' }}>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <motion.p className="text-lg" style={{ color: '#666666', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 500 }} initial={prefersReducedMotion ? { opacity: 1 } : { opacity: 0, y: 20 }} animate={isInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.8, ease: [0.4, 0.0, 0.2, 1] }}>Operating globally from Germany.<br />Serving companies, creators, and developers worldwide.</motion.p>
      </div>
    </section>
  )
}

const CTASection: React.FC<{ prefersReducedMotion: boolean }> = ({ prefersReducedMotion }) => {
  const ref = useRef(null)
  const isInView = useInView(ref, { once: false, amount: 0.5 })

  return (
    <section ref={ref} className="relative py-32" style={{ backgroundColor: 'transparent' }}>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <motion.h2 className="text-3xl md:text-4xl mb-8" style={{ color: '#1A1A1A', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 600, letterSpacing: '-0.02em' }} initial={prefersReducedMotion ? { opacity: 1 } : { opacity: 0, y: 20 }} animate={isInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.8, ease: [0.4, 0.0, 0.2, 1] }}>Explore our world</motion.h2>
        <motion.div className="flex items-center justify-center gap-6 flex-wrap" initial={prefersReducedMotion ? { opacity: 1 } : { opacity: 0, y: 20 }} animate={isInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.8, delay: 0.2, ease: [0.4, 0.0, 0.2, 1] }}>
          <span className="px-8 py-3 rounded-full text-base" style={{ backgroundColor: 'rgba(0, 0, 0, 0.05)', color: 'rgba(26, 26, 26, 0.4)', border: '1px solid rgba(0, 0, 0, 0.1)', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 500, filter: 'blur(1px)', userSelect: 'none', pointerEvents: 'none', cursor: 'not-allowed' }}>Software</span>
          <span className="px-8 py-3 rounded-full text-base" style={{ backgroundColor: 'rgba(0, 0, 0, 0.05)', color: 'rgba(26, 26, 26, 0.4)', border: '1px solid rgba(0, 0, 0, 0.1)', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 500, filter: 'blur(1px)', userSelect: 'none', pointerEvents: 'none', cursor: 'not-allowed' }}>Hardware</span>
          <Link to="/#services" className="px-8 py-3 rounded-full text-base transition-all duration-300" style={{ backgroundColor: 'rgba(0, 0, 0, 0.05)', color: '#1A1A1A', border: '1px solid rgba(0, 0, 0, 0.1)', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 500, textDecoration: 'none', display: 'inline-block' }} onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.08)'; e.currentTarget.style.borderColor = 'rgba(0, 0, 0, 0.15)' }} onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.05)'; e.currentTarget.style.borderColor = 'rgba(0, 0, 0, 0.1)' }}>Services</Link>
        </motion.div>
      </div>
    </section>
  )
}

const FooterSection: React.FC<{ isMobile: boolean }> = ({ isMobile }) => {
  const ref = useRef(null)
  const isInView = useInView(ref, { once: false, amount: 0.3 })

  if (isMobile) {
    return <PageMobileFooter {...aboutPageFooter} />
  }

  return (
    <footer ref={ref} className="relative" style={{ backgroundColor: '#0E0E0E', position: 'sticky', bottom: 0, zIndex: 1, paddingTop: '140px', paddingBottom: '40px', marginTop: '-80px' }}>
      {/* Floating Gradient Divider */}
      <motion.div
        className="h-px w-full bg-gradient-to-r from-transparent via-white/10 to-transparent"
        initial={{ scaleX: 0 }}
        animate={isInView ? { scaleX: 1 } : { scaleX: 0 }}
        transition={{ duration: 1, ease: 'easeOut' }}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section 1 - Let's Connect (Personal CTA) */}
        <div className="text-center mb-20">
          <motion.h2
            className="text-3xl md:text-4xl mb-6"
            style={{ color: '#FFFFFF', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 600, letterSpacing: '-0.02em' }}
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
            transition={{ duration: 0.8, delay: 0.2, ease: [0.4, 0.0, 0.2, 1] }}
          >
            Let's Build the Future Together
          </motion.h2>
          <motion.p
            className="text-lg mb-8 max-w-2xl mx-auto"
            style={{ color: 'rgba(255, 255, 255, 0.7)', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 400, lineHeight: '1.7' }}
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
            transition={{ duration: 0.8, delay: 0.4, ease: [0.4, 0.0, 0.2, 1] }}
          >
            Whether you're a creator, partner, or investor — we'd love to hear from you.
          </motion.p>
          <motion.div
            className="flex flex-col items-center gap-3 mb-8"
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
            transition={{ duration: 0.8, delay: 0.6, ease: [0.4, 0.0, 0.2, 1] }}
          >
            <a href="mailto:hello@mernaki.de" className="text-base transition-all duration-300" style={{ color: 'rgba(255, 255, 255, 0.8)', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 500, textDecoration: 'none' }} onMouseEnter={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 1)'} onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.8)'}>
              info@mernaki.de
            </a>
            <span className="text-base" style={{ color: 'rgba(255, 255, 255, 0.6)', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 400 }}>
              Munich, Germany
            </span>
          </motion.div>
          <motion.div
            className="flex items-center justify-center gap-4"
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
            transition={{ duration: 0.8, delay: 0.8, ease: [0.4, 0.0, 0.2, 1] }}
          >
            <Link to="/contact" className="px-6 py-2.5 rounded-full text-sm transition-all duration-300" style={{ backgroundColor: 'rgba(255, 255, 255, 0.08)', color: 'rgba(255, 255, 255, 0.9)', border: '1px solid rgba(255, 255, 255, 0.15)', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 500, textDecoration: 'none', display: 'inline-block' }} onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.12)'; e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.25)' }} onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.08)'; e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.15)' }}>
              Contact Us
            </Link>
            <span className="px-6 py-2.5 rounded-full text-sm" style={{ backgroundColor: 'rgba(255, 255, 255, 0.05)', color: 'rgba(255, 255, 255, 0.4)', border: '1px solid rgba(255, 255, 255, 0.1)', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 500, filter: 'blur(1px)', userSelect: 'none', pointerEvents: 'none' }}>
              Join the Team
            </span>
          </motion.div>
        </div>

        {/* Section 2 - Utility Footer (Slim Navigation) */}
        <motion.div
          className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-12 pb-12 border-b border-white/10"
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.8, delay: 1.0, ease: [0.4, 0.0, 0.2, 1] }}
        >
          {/* Column 1 - Company */}
          <div>
            <h3 className="text-sm mb-4" style={{ color: 'rgba(255, 255, 255, 0.9)', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.1em' }}>
              Company
            </h3>
            <ul className="space-y-2.5">
              <li><Link to="/about" className="text-sm transition-all duration-300" style={{ color: 'rgba(255, 255, 255, 0.6)', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 400, textDecoration: 'none' }} onMouseEnter={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.9)'} onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.6)'}>About Mernaki</Link></li>
              <li><Link to="/team" className="text-sm transition-all duration-300" style={{ color: 'rgba(255, 255, 255, 0.6)', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 400, textDecoration: 'none' }} onMouseEnter={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.9)'} onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.6)'}>Team</Link></li>
              <li><span className="text-sm" style={{ color: 'rgba(255, 255, 255, 0.6)', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 400 }}>Careers</span></li>
              <li><span className="text-sm" style={{ color: 'rgba(255, 255, 255, 0.6)', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 400 }}>Press</span></li>
            </ul>
          </div>

          {/* Column 2 - Divisions */}
          <div>
            <h3 className="text-sm mb-4" style={{ color: 'rgba(255, 255, 255, 0.9)', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.1em' }}>
              Divisions
            </h3>
            <ul className="space-y-2.5">
              <li><span className="text-sm" style={{ color: 'rgba(255, 255, 255, 0.4)', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 400, filter: 'blur(1.5px)', userSelect: 'none', pointerEvents: 'none' }}>Software</span></li>
              <li><span className="text-sm" style={{ color: 'rgba(255, 255, 255, 0.4)', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 400, filter: 'blur(1.5px)', userSelect: 'none', pointerEvents: 'none' }}>Hardware</span></li>
              <li><Link to="/#services" className="text-sm transition-all duration-300" style={{ color: 'rgba(255, 255, 255, 0.6)', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 400, textDecoration: 'none' }} onMouseEnter={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.9)'} onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.6)'}>Services</Link></li>
            </ul>
          </div>

          {/* Column 3 - Support */}
          <div>
            <h3 className="text-sm mb-4" style={{ color: 'rgba(255, 255, 255, 0.9)', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.1em' }}>
              Support
            </h3>
            <ul className="space-y-2.5">
              <li><Link to="/help" className="text-sm transition-all duration-300" style={{ color: 'rgba(255, 255, 255, 0.6)', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 400, textDecoration: 'none' }} onMouseEnter={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.9)'} onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.6)'}>Help Center</Link></li>
              <li><Link to="/contact" className="text-sm transition-all duration-300" style={{ color: 'rgba(255, 255, 255, 0.6)', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 400, textDecoration: 'none' }} onMouseEnter={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.9)'} onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.6)'}>Contact</Link></li>
              <li><Link to="/report-issue" className="text-sm transition-all duration-300" style={{ color: 'rgba(255, 255, 255, 0.6)', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 400, textDecoration: 'none' }} onMouseEnter={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.9)'} onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.6)'}>Report an Issue</Link></li>
              <li><span className="text-sm" style={{ color: 'rgba(255, 255, 255, 0.6)', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 400 }}>Privacy Policy</span></li>
              <li><Link to="/impressum" className="text-sm transition-all duration-300" style={{ color: 'rgba(255, 255, 255, 0.6)', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 400, textDecoration: 'none' }} onMouseEnter={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.9)'} onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.6)'}>Impressum</Link></li>
            </ul>
          </div>

          {/* Column 4 - Logo */}
          <div className="flex items-center gap-3 justify-center md:justify-end">
            <img src="/Mernaki_Logo_Pack/transparent/mernaki-128.png" alt="Mernaki Logo" className="object-contain" style={{ height: '42px', width: 'auto', opacity: 0.85, filter: 'brightness(0) invert(1)' }} loading="lazy" />
            <h3
              className="text-lg"
              style={{
                color: '#FFFFFF',
                fontFamily: "'Brush Script MT', 'Lucida Handwriting', 'Apple Chancery', cursive",
                fontWeight: 400
              }}
            >
              Mernaki
            </h3>
          </div>
        </motion.div>

        {/* Bottom Line */}
        <motion.div
          className="text-center"
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : { opacity: 0 }}
          transition={{ duration: 0.8, delay: 1.2, ease: [0.4, 0.0, 0.2, 1] }}
        >
          <p className="text-sm" style={{ color: 'rgba(255, 255, 255, 0.4)', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 300 }}>
            © {new Date().getFullYear()} Mernaki. All rights reserved. | Munich, Germany
          </p>
        </motion.div>
      </div>
    </footer>
  )
}
